import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ThemeToggle } from '../components/ThemeProvider';
import { useAuthStore, useLanguageStore, usePremiumStore } from '../store';
import { ArrowRight, Loader2, AlertCircle, X, Mail, Lock, User, Gift, CheckCircle2, Eye, EyeOff, Check } from 'lucide-react';

/**
 * LoginPage Component - Email/Password Authentication
 * 
 * Simple and reliable authentication without Google OAuth dependencies
 */

// Safe JSON helper: avoids SyntaxError when server returns HTML (502/503 proxy errors)
async function safeJson(response, fallback = 'Server vorübergehend nicht erreichbar. Bitte versuche es erneut.') {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(fallback);
  }
}

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, checkAuth, user, setUser } = useAuthStore();
  const { t, currentLanguage } = useLanguageStore();
  const fetchPremiumStatus = usePremiumStore((s) => s.fetchStatus);
  
  // Form state
  const [isLogin, setIsLogin] = useState(true); // true = login, false = register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isProcessingRef = useRef(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // 3-step signup flow state
  const [signupStep, setSignupStep] = useState(1); // 1: email, 2: code, 3: account setup
  const [verificationToken, setVerificationToken] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRefs = useRef([]);

  // Check for referral code in URL or sessionStorage or localStorage
  useEffect(() => {
    // If user has a cached session and is offline → go straight to app
    if (!navigator.onLine) {
      const cachedToken = localStorage.getItem('mentis_session_token');
      const cachedUserRaw = localStorage.getItem('mentis-auth');
      if (cachedToken && cachedUserRaw) {
        try {
          const cached = JSON.parse(cachedUserRaw);
          if (cached?.state?.isAuthenticated && cached?.state?.user) {
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch {}
      }
    }

    const refCode = searchParams.get('ref') || searchParams.get('referral');
    if (refCode) {
      const upperCode = refCode.toUpperCase();
      setReferralCode(upperCode);
      setIsLogin(false); // Switch to register mode
      
      // Store in BOTH storages for persistence
      sessionStorage.setItem('mentis_referral_code', upperCode);
      localStorage.setItem('mentis_referral_code', upperCode);
      
      // Validate the referral code
      validateReferralCode(upperCode);
    } else {
      // Check sessionStorage first, then localStorage for stored referral code
      const storedCode = sessionStorage.getItem('mentis_referral_code') || 
                        localStorage.getItem('mentis_referral_code');
      if (storedCode) {
        setReferralCode(storedCode);
        setIsLogin(false);
        validateReferralCode(storedCode);
      }
    }
  }, [searchParams]);

  // Validate referral code function
  const validateReferralCode = async (code) => {
    try {
      const apiUrl = window.location.origin;
      const response = await fetch(`${apiUrl}/api/referral/validate/${code}`);
      const data = await safeJson(response);
      
      if (data.valid) {
        // Show success message
        console.log('[LoginPage] Valid referral code:', data.message);
      }
    } catch (error) {
      console.log('[LoginPage] Referral validation error:', error);
    }
  };

  // Check if already authenticated — run once on mount only
  // Using isAuthenticated as a simple flag avoids re-running on every user object change
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/dashboard', { replace: true });
      return;
    }
    // Only check auth from server if we have no cached state
    checkAuth().then(verifiedUser => {
      if (verifiedUser) navigate('/dashboard', { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount — auth state is managed globally by the store

  // Handle URL error params
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isProcessingRef.current || isLoading) return;

    // Offline check
    if (!navigator.onLine) {
      setError('Keine Internetverbindung. Bitte versuche es erneut wenn du online bist.');
      return;
    }
    
    isProcessingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = window.location.origin;
      // Login only — register uses the 3-step wizard
      const body = { email, password, remember_me: rememberMe };

      const response = await fetch(apiUrl + '/api/auth/email/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await safeJson(response);

      if (!response.ok) {
        throw new Error(data.detail || t('errors.unauthorized'));
      }

      localStorage.setItem('mentis_session_token', data.token);
      setUser(data.user);
      fetchPremiumStatus().catch(() => {});
      navigate('/dashboard', { replace: true });
      
    } catch (err) {
      if (!navigator.onLine || err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError') || err.message?.includes('Load failed')) {
        setError('Keine Internetverbindung. Bitte prüfe deine WLAN-Verbindung und versuche es erneut.');
      } else {
        setError(err.message || t('errors.unauthorized'));
      }
    } finally {
      isProcessingRef.current = false;
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError(null);
    setSignupStep(1);
    setCodeDigits(['', '', '', '', '']);
    setVerificationToken('');
    setPassword('');
  };

  // Countdown timer for code resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // OTP input helpers
  const handleCodeInput = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...codeDigits];
    newDigits[index] = value.slice(-1);
    setCodeDigits(newDigits);
    if (value && index < 4) codeInputRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    if (text.length === 5) {
      setCodeDigits(text.split(''));
      setTimeout(() => codeInputRefs.current[4]?.focus(), 10);
    }
    e.preventDefault();
  };

  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (isProcessingRef.current || isLoading || !email.trim()) return;
    if (!navigator.onLine) { setError('Keine Internetverbindung.'); return; }
    isProcessingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(window.location.origin + '/api/auth/email/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), lang: currentLanguage || 'de' }),
      });
      const data = await safeJson(response, 'Fehler beim Senden des Codes');
      if (!response.ok) throw new Error(data.detail || 'Fehler beim Senden des Codes');
      setSignupStep(2);
      setCodeDigits(['', '', '', '', '']);
      setResendCooldown(60);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 150);
    } catch (err) {
      setError(err.message);
    } finally {
      isProcessingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    const code = codeDigits.join('');
    if (code.length < 5 || isProcessingRef.current || isLoading) return;
    isProcessingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(window.location.origin + '/api/auth/email/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code }),
      });
      const data = await safeJson(response, t('auth.codeInvalid'));
      if (!response.ok) throw new Error(data.detail || t('auth.codeInvalid'));
      setVerificationToken(data.verification_token);
      setSignupStep(3);
    } catch (err) {
      setError(err.message);
      setCodeDigits(['', '', '', '', '']);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 50);
    } finally {
      isProcessingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    if (isProcessingRef.current || isLoading) return;
    isProcessingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(window.location.origin + '/api/auth/email/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_token: verificationToken,
          password,
          name: name.trim() || 'User',
          referral_code: referralCode.trim() || undefined,
        }),
      });
      const data = await safeJson(response, 'Registrierung fehlgeschlagen');
      if (!response.ok) throw new Error(data.detail || 'Registrierung fehlgeschlagen');
      localStorage.setItem('mentis_session_token', data.token);
      sessionStorage.removeItem('mentis_referral_code');
      localStorage.removeItem('mentis_referral_code');
      setUser(data.user);
      fetchPremiumStatus().catch(() => {});
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      isProcessingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {    e.preventDefault();
    if (resetPassword !== resetConfirm) {
      setError(t('auth.passwordsDontMatch'));
      return;
    }
    if (resetPassword.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    setResetLoading(true);
    setError(null);
    try {
      const response = await fetch(window.location.origin + '/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, new_password: resetPassword })
      });
      const data = await safeJson(response, 'Passwort zurücksetzen fehlgeschlagen');
      if (!response.ok) throw new Error(data.detail || 'Fehler');
      setResetSuccess(true);
      // Pre-fill login form after 2s
      setTimeout(() => {
        setEmail(resetEmail);
        setPassword(resetPassword);
        setShowResetForm(false);
        setResetSuccess(false);
        setResetEmail('');
        setResetPassword('');
        setResetConfirm('');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
      
      {/* Theme toggle */}
      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Card className="border-border/50 shadow-xl">
          <CardHeader className="text-center pb-2">
            <img 
              src="/logo.png" 
              alt="Mentis" 
              className="mx-auto w-16 h-16 rounded-2xl object-cover mb-4" 
            />
            <CardTitle className="font-heading text-2xl">
              {isLogin
                ? t('auth.welcomeBack')
                : signupStep === 2
                  ? t('auth.signupStep2Title')
                  : signupStep === 3
                    ? t('auth.signupStep3Title')
                    : t('auth.createAccount')}
            </CardTitle>
            <p className="text-muted-foreground mt-2 text-sm">
              {isLogin
                ? t('auth.loginSubtitle')
                : signupStep === 2
                  ? t('auth.codeSentTo').replace('{email}', email)
                  : signupStep === 3
                    ? t('auth.step3of3')
                    : t('auth.codeHint')}
            </p>
            {!isLogin && (
              <div className="flex items-center justify-center gap-2 mt-3">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all
                      ${signupStep > s
                        ? 'bg-primary/20 text-primary'
                        : signupStep === s
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted text-muted-foreground'}`}
                  >
                    {signupStep > s ? <Check className="w-3 h-3" /> : s}
                  </div>
                ))}
              </div>
            )}
          </CardHeader>
          
          <CardContent className="space-y-6 pt-4">
            {/* Error display */}
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-destructive/10 border border-destructive/20 rounded-lg p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-destructive font-medium">{t('common.error')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{error}</p>
                  </div>
                  <button 
                    onClick={() => setError(null)} 
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Auth Form — Login (unchanged) or 3-step Signup Wizard */}
            {isLogin ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email field */}
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      disabled={isLoading}
                      data-testid="email-input"
                    />
                  </div>
                </div>

                {/* Password field */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <button
                      type="button"
                      onClick={() => { setShowResetForm(true); setResetEmail(email); setError(null); }}
                      className="text-xs text-primary hover:underline"
                      data-testid="forgot-password-btn"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      minLength={6}
                      disabled={isLoading}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    id="remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                    data-testid="remember-me-checkbox"
                  />
                  <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer select-none">
                    Angemeldet bleiben (30 Tage)
                  </label>
                </div>

                <Button type="submit" disabled={isLoading} className="w-full rounded-full h-12 text-base" data-testid="auth-submit-btn">
                  {isLoading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('auth.loginLoading')}</>
                  ) : (
                    <><Mail className="w-5 h-5 mr-2" />{t('auth.login')}<ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>
              </form>
            ) : (
              /* ── 3-Step Signup Wizard ─────────────────────────────────── */
              <div className="space-y-4">

                {/* STEP 1: Email eingeben */}
                {signupStep === 1 && (
                  <motion.form
                    key="signup-step1"
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleSendCode}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">{t('auth.email')}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder={t('auth.emailPlaceholder')}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                          disabled={isLoading}
                          data-testid="email-input"
                          autoComplete="email"
                        />
                      </div>
                    </div>
                    <Button type="submit" disabled={isLoading || !email.trim()} className="w-full rounded-full h-12 text-base" data-testid="send-code-btn">
                      {isLoading
                        ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('auth.codeSending')}</>
                        : <><Mail className="w-5 h-5 mr-2" />{t('auth.sendCode')}<ArrowRight className="w-5 h-5 ml-2" /></>}
                    </Button>
                  </motion.form>
                )}

                {/* STEP 2: Code eingeben */}
                {signupStep === 2 && (
                  <motion.form
                    key="signup-step2"
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleVerifyCode}
                    className="space-y-5"
                  >
                    {/* 5 OTP boxes */}
                    <div className="flex gap-2 justify-center" onPaste={handleCodePaste}>
                      {[0, 1, 2, 3, 4].map((i) => (
                        <input
                          key={i}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={codeDigits[i]}
                          onChange={(e) => handleCodeInput(i, e.target.value)}
                          onKeyDown={(e) => handleCodeKeyDown(i, e)}
                          ref={(el) => (codeInputRefs.current[i] = el)}
                          className={`w-12 h-14 text-center text-2xl font-mono font-bold rounded-xl border-2 transition-all
                            bg-background outline-none
                            ${codeDigits[i] ? 'border-primary text-foreground' : 'border-border text-muted-foreground'}
                            focus:border-primary focus:ring-2 focus:ring-primary/20`}
                          data-testid={`code-input-${i}`}
                          autoComplete="off"
                          disabled={isLoading}
                        />
                      ))}
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading || codeDigits.join('').length < 5}
                      className="w-full rounded-full h-12 text-base"
                      data-testid="verify-code-btn"
                    >
                      {isLoading
                        ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('auth.codeVerifying')}</>
                        : <><Check className="w-5 h-5 mr-2" />{t('auth.enterCode')}<ArrowRight className="w-5 h-5 ml-2" /></>}
                    </Button>

                    {/* Resend + Back */}
                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => { setSignupStep(1); setCodeDigits(['', '', '', '', '']); setError(null); }}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="back-to-email-btn"
                      >
                        ← {t('auth.backToEmail')}
                      </button>
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={resendCooldown > 0 || isLoading}
                        className={`transition-colors ${resendCooldown > 0 ? 'text-muted-foreground cursor-not-allowed' : 'text-primary hover:underline'}`}
                        data-testid="resend-code-btn"
                      >
                        {resendCooldown > 0 ? `${t('auth.resendCode')} (${resendCooldown}s)` : t('auth.resendCode')}
                      </button>
                    </div>
                  </motion.form>
                )}

                {/* STEP 3: Account einrichten */}
                {signupStep === 3 && (
                  <motion.form
                    key="signup-step3"
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleRegister}
                    className="space-y-4"
                  >
                    {/* Display Name */}
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">{t('auth.displayName')}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder={t('auth.namePlaceholder')}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="pl-10"
                          disabled={isLoading}
                          data-testid="name-input"
                          autoComplete="name"
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">{t('auth.password')}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                        <Input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('auth.minCharsPlaceholder')}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                          minLength={6}
                          disabled={isLoading}
                          data-testid="password-input"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Referral Code (optional) */}
                    <div className="space-y-2">
                      <Label htmlFor="referral">{t('auth.referralCode')} <span className="text-muted-foreground text-xs">({t('common.optional') || 'optional'})</span></Label>
                      <div className="relative">
                        <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="referral"
                          type="text"
                          placeholder={t('auth.referralCodePlaceholder')}
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                          disabled={isLoading}
                          data-testid="referral-input"
                          className="pl-10 font-mono"
                        />
                      </div>
                      {referralCode && (
                        <p className="text-xs text-muted-foreground">{t('auth.referralBonus')}</p>
                      )}
                    </div>

                    <Button type="submit" disabled={isLoading || !password} className="w-full rounded-full h-12 text-base" data-testid="auth-submit-btn">
                      {isLoading
                        ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('auth.registerLoading')}</>
                        : <>{t('auth.createAccount')}<ArrowRight className="w-5 h-5 ml-2" /></>}
                    </Button>
                  </motion.form>
                )}
              </div>
            )}

            {/* Divider + toggle only for login or step 1 of signup */}
            {(isLogin || signupStep === 1) && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      {isLogin ? t('auth.newToMentis') : t('auth.alreadyRegistered')}
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={toggleMode}
                  className="w-full rounded-full"
                  disabled={isLoading}
                  data-testid="toggle-auth-mode"
                >
                  {isLogin ? t('auth.createFreeAccount') : t('auth.goToLogin')}
                </Button>
              </>
            )}
            
            {/* Back to landing */}
            <div className="text-center">
              <button 
                onClick={() => navigate('/')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('auth.backToStart')}
              </button>
            </div>
            
            {/* Terms */}
            <p className="text-xs text-center text-muted-foreground">
              {t('auth.termsText')}
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Password Reset Modal */}
      <AnimatePresence>
        {showResetForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm"
            >
              <Card className="border-border/50 shadow-xl">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{t('auth.resetPassword')}</CardTitle>
                    <button onClick={() => { setShowResetForm(false); setError(null); setResetSuccess(false); }}>
                      <X className="w-5 h-5 text-muted-foreground hover:text-foreground" />
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('auth.resetPasswordSubtitle')}</p>
                </CardHeader>
                <CardContent>
                  {resetSuccess ? (
                    <div className="flex flex-col items-center gap-3 py-4 text-center">
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                      <p className="font-medium text-green-600">{t('auth.resetPasswordSuccess')}</p>
                      <p className="text-sm text-muted-foreground">{t('auth.autoLoginAfterReset')}</p>
                    </div>
                  ) : (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                      {error && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                          {error}
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>{t('auth.email')}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="email"
                            value={resetEmail}
                            onChange={e => setResetEmail(e.target.value)}
                            placeholder={t('auth.emailPlaceholder')}
                            className="pl-10"
                            required
                            data-testid="reset-email-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('auth.newPassword')}</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                          <Input
                            type={showResetPassword ? "text" : "password"}
                            value={resetPassword}
                            onChange={e => setResetPassword(e.target.value)}
                            placeholder={t('auth.minCharsPlaceholder')}
                            className="pl-10 pr-10"
                            required
                            minLength={6}
                            data-testid="reset-password-input"
                          />
                          <button type="button" onClick={() => setShowResetPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showResetPassword ? 'Passwort verbergen' : 'Passwort anzeigen'} tabIndex={-1}>
                            {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('auth.confirmPassword')}</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                          <Input
                            type={showResetConfirm ? "text" : "password"}
                            value={resetConfirm}
                            onChange={e => setResetConfirm(e.target.value)}
                            placeholder={t('auth.repeatPasswordPlaceholder')}
                            className="pl-10 pr-10"
                            required
                            data-testid="reset-confirm-input"
                          />
                          <button type="button" onClick={() => setShowResetConfirm(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showResetConfirm ? 'Passwort verbergen' : 'Passwort anzeigen'} tabIndex={-1}>
                            {showResetConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <Button type="submit" className="w-full" disabled={resetLoading} data-testid="reset-submit-btn">
                        {resetLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('auth.resetting')}</> : t('auth.resetPassword')}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginPage;
